// Agent Lounge API client (QAL-T3).
//
// Isolated from the shared services/api.js (small PR surface, no risk to the
// shared axios instance) — mirrors services/whatsNewService.js. Paths + shapes
// are the fixed API contract (lounge app, TICKET3_WORKORDER "API-VERTRAG"),
// never guessed:
//   channels → GET  /api/lounge/channels/         [{id, name, slug, short_name, description, order}]
//   feed     → GET  /api/lounge/posts/?channel=<slug>&sort=latest|top&page=N (DRF-paginated)
//   detail   → GET  /api/lounge/posts/<id>/       (single post object, T4 base)
//   create   → POST /api/lounge/posts/            body {text, channel}
//   comments → GET  /api/lounge/posts/<id>/comments/   {count, results} — top-level
//              chronological, one nested `replies` level (T4 contract §1)
//   comment  → POST /api/lounge/posts/<id>/comments/   body {text, parent?} (§2)
//   vote     → POST /api/lounge/posts/<id>/vote/        body {value: 1|-1}
//              POST /api/lounge/comments/<id>/vote/     (T5 contract §1)
//              Toggle semantics live SERVER-side: same value removes the
//              vote, other value switches, none creates. Response 200 is
//              EXACTLY {score, my_vote}.
//   edit     → PATCH /api/lounge/posts/<id>/            body {text?, channel?}
//              PATCH /api/lounge/comments/<id>/         body {text}
//              Own content only (foreign → 403); sets edited_at; response =
//              the full object (T6 contract §1).
//   delete   → DELETE /api/lounge/posts|comments/<id>/  → 204 soft-delete,
//              own content only (T6 contract §2).
//   files    → POST /api/lounge/posts/ goes multipart when files ride along
//              (T7 contract §1); delivery ONLY via
//              GET /api/lounge/attachments/<id>/file/ → 302 presigned
//              (fetchAttachmentBlob — post payloads never carry URLs, §2)
//   report   → POST /api/lounge/posts|comments/<id>/report/ body {reason}
//              (3 fixed T1 codes: named_entity | not_craft | abusive) →
//              201 created / 200 idempotent repeat. DELETE .../report/ =
//              undo own report → 204 (T6 contract §3).
//
// CHANNEL VARIANT (documented decision per contract §4): createPost sends the
// channel SLUG — the same identifier the feed filter uses (?channel=<slug>),
// so FE state carries exactly one channel key. BE must accept the slug.
//
// Anonymity rule (contract §2): responses never carry raw up/down counts or
// voter identities — only `score` (capped at 0) and the requester's own
// `my_vote`. This client never asks for more.
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

const authApi = axios.create({ baseURL: API_BASE_URL, withCredentials: true });

const readAccessToken = () => {
  try {
    return JSON.parse(localStorage.getItem("tokens") || "null")?.access || null;
  } catch (e) {
    return null; /* no token — the server returns 401 */
  }
};

// Same Bearer-token attachment the whats-new / scout-request clients use.
authApi.interceptors.request.use((config) => {
  const access = readAccessToken();
  if (access) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${access}`;
  }
  return config;
});

const fail = (error) => ({
  success: false,
  status: error?.response?.status,
  error:
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    (typeof error?.response?.data === "object" && error.response.data
      ? Object.values(error.response.data)[0]
      : null) ||
    "Something went wrong. Please try again.",
});

const isCancel = (error) =>
  error?.name === "CanceledError" || error?.name === "AbortError";

// T7 blob cache — id → {promise, refs}. Module-level so feed + detail share
// one download per attachment.
const attachmentBlobs = new Map();

export const loungeService = {
  // Active channels, ordered by `order` (server-side). Used for the pills and
  // the composer's channel selector.
  getChannels: async (signal) => {
    try {
      const res = await authApi.get("/api/lounge/channels/", { signal });
      return { success: true, channels: Array.isArray(res.data) ? res.data : [] };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // Paginated feed. `channel` is the slug (omit for All), `sort` is
  // "latest" (default) or "top". Returns the raw DRF envelope fields so the
  // page can drive load-more off `next`.
  getPosts: async ({ channel, sort, page = 1 } = {}, signal) => {
    try {
      const params = { page };
      if (channel) params.channel = channel;
      if (sort) params.sort = sort;
      const res = await authApi.get("/api/lounge/posts/", { params, signal });
      return {
        success: true,
        results: res.data?.results || [],
        count: res.data?.count ?? 0,
        next: res.data?.next ?? null,
        previous: res.data?.previous ?? null,
      };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // Single post (T4 detail base). 404 for soft-deleted/unknown ids.
  getPost: async (id, signal) => {
    try {
      const res = await authApi.get(`/api/lounge/posts/${id}/`, { signal });
      return { success: true, post: res.data };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // Create a post. `channel` = slug (see header). Author is always derived
  // from the request user server-side — never sent from here.
  //
  // T7 (contract §1): with `files` (File[]) the body becomes multipart —
  // text + channel + every file under `files`. The server validates (max 3
  // images OR 1 pdf, never mixed, ≤10 MB each); without files the JSON body
  // stays EXACTLY as before (T3 behaviour untouched).
  createPost: async ({ text, channel, files } = {}) => {
    try {
      let body = { text, channel };
      if (Array.isArray(files) && files.length > 0) {
        body = new FormData();
        body.append("text", text);
        body.append("channel", channel);
        files.forEach((file) => body.append("files", file));
      }
      const res = await authApi.post("/api/lounge/posts/", body);
      return { success: true, post: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Comments of a post (T4 contract §1): top-level chronological, each with
  // its `replies` array (one level, chronological). Soft-deleted comments
  // never arrive. 404 for soft-deleted/unknown posts.
  getComments: async (postId, signal) => {
    try {
      const res = await authApi.get(`/api/lounge/posts/${postId}/comments/`, {
        signal,
      });
      return {
        success: true,
        count: res.data?.count ?? 0,
        results: res.data?.results || [],
      };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // Create a comment (T4 contract §2). `parent` = id of a TOP-LEVEL comment
  // for replies, omitted for top-level comments. The server enforces the
  // single reply level (reply-on-reply → 400) and derives the author from
  // the request user — never sent from here.
  createComment: async (postId, { text, parent }) => {
    try {
      const body = { text };
      if (parent != null) body.parent = parent;
      const res = await authApi.post(
        `/api/lounge/posts/${postId}/comments/`,
        body,
      );
      return { success: true, comment: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Vote on a post (T5 contract §1). value ∈ {1, -1}; the SERVER owns the
  // toggle (same value → vote removed, my_vote null). Returns the two
  // contract keys — score (capped at 0) + my_vote — nothing else exists
  // in the response (anonymity rule).
  votePost: async (id, value) => {
    try {
      const res = await authApi.post(`/api/lounge/posts/${id}/vote/`, { value });
      return {
        success: true,
        score: res.data?.score ?? 0,
        my_vote: res.data?.my_vote ?? null,
      };
    } catch (error) {
      return fail(error);
    }
  },

  // Vote on a comment or reply — same contract as votePost.
  voteComment: async (id, value) => {
    try {
      const res = await authApi.post(`/api/lounge/comments/${id}/vote/`, {
        value,
      });
      return {
        success: true,
        score: res.data?.score ?? 0,
        my_vote: res.data?.my_vote ?? null,
      };
    } catch (error) {
      return fail(error);
    }
  },

  // ——— T6: edit / delete / report ———

  // Edit an OWN post (T6 §1). `channel` = slug, both fields optional — only
  // the provided ones are sent. Response is the full post object with
  // edited_at set.
  updatePost: async (id, { text, channel } = {}) => {
    try {
      const body = {};
      if (text !== undefined) body.text = text;
      if (channel !== undefined) body.channel = channel;
      const res = await authApi.patch(`/api/lounge/posts/${id}/`, body);
      return { success: true, post: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Edit an OWN comment (T6 §1) — text only, no channel on comments.
  updateComment: async (id, { text } = {}) => {
    try {
      const res = await authApi.patch(`/api/lounge/comments/${id}/`, { text });
      return { success: true, comment: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Soft-delete an OWN post (T6 §2) — 204, post + whole comment thread
  // disappear for everyone.
  deletePost: async (id) => {
    try {
      await authApi.delete(`/api/lounge/posts/${id}/`);
      return { success: true };
    } catch (error) {
      return fail(error);
    }
  },

  // Soft-delete an OWN comment (T6 §2) — a dead top-level hides its replies.
  deleteComment: async (id) => {
    try {
      await authApi.delete(`/api/lounge/comments/${id}/`);
      return { success: true };
    } catch (error) {
      return fail(error);
    }
  },

  // Report a FOREIGN post (T6 §3). `reason` ∈ named_entity|not_craft|abusive.
  // 201 first time, 200 idempotent repeat — both are success here.
  reportPost: async (id, reason) => {
    try {
      await authApi.post(`/api/lounge/posts/${id}/report/`, { reason });
      return { success: true };
    } catch (error) {
      return fail(error);
    }
  },

  // Report a FOREIGN comment — same contract as reportPost.
  reportComment: async (id, reason) => {
    try {
      await authApi.post(`/api/lounge/comments/${id}/report/`, { reason });
      return { success: true };
    } catch (error) {
      return fail(error);
    }
  },

  // Undo the requester's own report (T6 §3) — 204, object visible again.
  // `kind` ∈ "post" | "comment".
  undoReport: async (kind, id) => {
    try {
      const base = kind === "comment" ? "comments" : "posts";
      await authApi.delete(`/api/lounge/${base}/${id}/report/`);
      return { success: true };
    } catch (error) {
      return fail(error);
    }
  },

  // ——— T8: activity (lounge-only notifications) ———
  // Own reply/mention notifications, newest first, page_size 15 (contract §2).
  // Shape: {count, unread_count, results:[{id, type, actor:{name,
  // profile_picture_url, is_fifa_verified}, post_id, comment_id,
  // text_excerpt, created_at, read_at}]}. `unreadCount` is the badge number.
  getActivity: async (page = 1, signal) => {
    try {
      const res = await authApi.get("/api/lounge/activity/", {
        params: { page },
        signal,
      });
      return {
        success: true,
        count: res.data?.count ?? 0,
        unreadCount: res.data?.unread_count ?? 0,
        next: res.data?.next ?? null,
        results: res.data?.results || [],
      };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // Mark activity read (contract §3). No ids → mark ALL read; `ids` array →
  // mark those. Response carries the fresh {unread_count} for the badge.
  markActivityRead: async (ids) => {
    try {
      const body = Array.isArray(ids) && ids.length > 0 ? { ids } : {};
      const res = await authApi.post("/api/lounge/activity/read/", body);
      return { success: true, unreadCount: res.data?.unread_count ?? 0 };
    } catch (error) {
      return fail(error);
    }
  },

  // ——— T7: attachment delivery ———
  // The payload NEVER carries S3 URLs (contract §2); the only door is
  // GET /api/lounge/attachments/<id>/file/ (permission-checked, 302 to a
  // short-lived presigned URL). fetchAttachmentBlob walks through that door
  // with the Bearer token, follows the redirect and hands back an object
  // URL. Cached per attachment id and REF-COUNTED: every fetch call pairs
  // with one releaseAttachmentBlob (component unmount) — the object URL is
  // revoked when the last holder lets go.
  fetchAttachmentBlob: (id) => {
    let entry = attachmentBlobs.get(id);
    if (!entry) {
      const access = readAccessToken();
      const promise = fetch(`${API_BASE_URL}/api/lounge/attachments/${id}/file/`, {
        headers: access ? { Authorization: `Bearer ${access}` } : {},
        // Browsers follow the 302 to S3 themselves and strip the
        // Authorization header on the cross-origin hop — the presigned URL
        // needs none.
        redirect: "follow",
      }).then(async (res) => {
        if (!res.ok) throw new Error(`attachment ${id}: HTTP ${res.status}`);
        return URL.createObjectURL(await res.blob());
      });
      entry = { promise, refs: 0 };
      attachmentBlobs.set(id, entry);
      // A failed fetch never stays cached — the next mount retries.
      promise.catch(() => {
        if (attachmentBlobs.get(id) === entry) attachmentBlobs.delete(id);
      });
    }
    entry.refs += 1;
    return entry.promise;
  },

  releaseAttachmentBlob: (id) => {
    const entry = attachmentBlobs.get(id);
    if (!entry) return;
    entry.refs -= 1;
    if (entry.refs <= 0) {
      attachmentBlobs.delete(id);
      entry.promise.then((url) => URL.revokeObjectURL(url)).catch(() => {});
    }
  },
};

export default loungeService;
